import { useCallback, useEffect, useState } from 'react';

import { ErroRespostaHttp } from '../../../shared/api/http';
import { definirMaterialChave, listarMateriaisChave } from '../api/conteudo';
import type { MateriaisChave } from '../api/conteudo';

/**
 * Modo Versus: qual material da biblioteca libera a resposta de cada chat.
 * Com um material indicado, a IA só entrega a resposta do case quando o grupo anexa
 * exatamente ele; sem indicação, o chat da rodada é livre.
 */
export function MaterialChavePainel({ csrfToken }: { csrfToken: string }) {
    const [dados, setDados] = useState<MateriaisChave | null>(null);
    const [erroDeCarga, setErroDeCarga] = useState(false);
    const [salvando, setSalvando] = useState<string | null>(null);
    const [aviso, setAviso] = useState<{ texto: string; erro: boolean } | null>(null);

    const carregar = useCallback(async () => {
        try {
            const recebido = await listarMateriaisChave();
            // Servidor antigo, sem esta rota, devolve outra coisa: trata como falha de carga.
            if (!Array.isArray(recebido?.rodadas) || !Array.isArray(recebido?.materiais)) throw new Error('formato');
            setDados(recebido);
            setErroDeCarga(false);
        } catch {
            setErroDeCarga(true);
        }
    }, []);

    useEffect(() => {
        void carregar();
    }, [carregar]);

    async function escolher(rodada: MateriaisChave['rodadas'][number], valor: string) {
        const materialId = valor === '' ? null : valor;
        const titulo = dados?.materiais.find((m) => m.id === materialId)?.titulo;
        const pergunta = materialId
            ? `Definir «${titulo}» como o material que libera a resposta da ${rodada.local}?`
            : `Tirar o material que libera a resposta da ${rodada.local}? O chat dessa rodada volta a ser livre.`;
        if (!window.confirm(pergunta)) return;
        setSalvando(rodada.atividade_id);
        setAviso(null);
        try {
            await definirMaterialChave(rodada.atividade_id, materialId, csrfToken);
            setAviso({ texto: `${rodada.local}: salvo.`, erro: false });
        } catch (erro) {
            const corpo = erro instanceof ErroRespostaHttp ? erro.corpo : null;
            const texto = typeof corpo?.mensagem === 'string' ? corpo.mensagem : 'Não foi possível salvar. Verifique a conexão e tente de novo.';
            setAviso({ texto, erro: true });
        }
        setSalvando(null);
        await carregar();
    }

    if (erroDeCarga) {
        return (
            <section className="grupo-variaveis" aria-label="Material que libera a resposta">
                <p className="mensagem-login">
                    Não foi possível carregar o material de cada rodada.{' '}
                    <button className="botao-texto" type="button" onClick={() => void carregar()}>Tentar novamente</button>
                </p>
            </section>
        );
    }
    if (!dados) return null;

    return (
        <section className="grupo-variaveis" aria-label="Material que libera a resposta">
            <h3>Material que libera a resposta</h3>
            <p className="motivo-estrutura">
                Em cada rodada, a IA só entrega a resposta do case quando o grupo anexa este material no chat.
                Sem material indicado, o chat da rodada é livre.
            </p>
            {dados.rodadas.map((rodada) => {
                const id = `material-chave-${rodada.atividade_id}`;
                return (
                    <div key={rodada.atividade_id} className="caixa-variavel">
                        <label htmlFor={id}>{rodada.local}</label>
                        <select
                            id={id}
                            className="seletor-material-chave"
                            value={rodada.material_id ?? ''}
                            disabled={!dados.editavel || salvando !== null}
                            onChange={(e) => void escolher(rodada, e.target.value)}
                        >
                            <option value="">Nenhum (chat livre)</option>
                            {dados.materiais.map((material) => (
                                <option key={material.id} value={material.id}>
                                    {material.titulo}{material.tem_arquivo ? '' : ' (sem PDF)'}
                                </option>
                            ))}
                        </select>
                    </div>
                );
            })}
            {aviso && <p className={aviso.erro ? 'mensagem-login' : 'mensagem-salvo'} role="status">{aviso.texto}</p>}
        </section>
    );
}
